import { isReflectionColumn, type ColumnId } from './domain.js';
import type { PublicRetroNote, PublicRetroRoom } from './view.js';

const COLUMN_HEADINGS: Record<ColumnId, string> = {
  wentWell: 'Went well',
  toImprove: 'To improve',
  actionItems: 'Action items',
};

const COLUMN_ORDER: readonly ColumnId[] = ['wentWell', 'toImprove', 'actionItems'];

function renderNote(note: PublicRetroNote): string {
  const votes =
    isReflectionColumn(note.column) && note.voteCount !== null
      ? ` (${note.voteCount} vote${note.voteCount === 1 ? '' : 's'})`
      : '';
  // A concealed note (should not normally reach this export) carries no fragments to attribute,
  // so there is nothing to list here at all.
  if (note.fragments === null) return `- Anonymous${votes}`;
  // A merged note has one fragment per originally-written contribution, each with its own
  // author. Votes belong to the whole note, not any one fragment, so they trail the block
  // rather than riding on the last line — indenting keeps every line one markdown list item.
  const lines = note.fragments.map((fragment) => `${fragment.text} — ${fragment.authorName ?? 'Anonymous'}`);
  return note.fragments.length > 1
    ? `- ${lines.join('\n  ')}${votes ? `\n  ${votes.trim()}` : ''}`
    : `- ${lines[0]}${votes}`;
}

/**
 * The board read back as plain text — pure, tested without a DOM. Reflection
 * columns first, action items last, matching the board's own reading order.
 */
export function retroAsMarkdown(room: PublicRetroRoom): string {
  const sections = COLUMN_ORDER.map((column) => {
    const notes = room.notes.filter((note) => note.column === column);
    const body = notes.length > 0 ? notes.map(renderNote).join('\n') : '_Nothing here._';
    return `## ${COLUMN_HEADINGS[column]}\n\n${body}`;
  });

  return [`# ${room.title}`, ...sections].join('\n\n');
}
